import { fail, ok, type Result } from './result.js';

/**
 * Item 11 do lote de 19/10 (chat do site no Conversas): os domínios de um widget e a conferência da `Origin` de quem o usa.
 * A mesma regra vale no cadastro (módulo Conversas) e na entrada pública (API), por isso fica aqui.
 *
 * Cada domínio é um nome de host (`malgo.com.br`, `www.malgo.com.br`), com porta se não for a padrão (`loja.com.br:8443`),
 * ou o curinga de subdomínios (`*.malgo.com.br`: vale para `www.malgo.com.br` e `a.b.malgo.com.br`, não para o próprio
 * `malgo.com.br`, que entra à parte). Quem cola o endereço inteiro (`https://www.malgo.com.br/contato`) fica com o host:
 * o widget vale para o site, não para uma página. Endereço IP não entra (o site do cliente tem nome); a máquina local
 * (`localhost`, `127.0.0.1`) entra para o desenvolvimento do site.
 */
export const MAX_DOMINIOS_WIDGET = 20;
/** As 6 cores de destaque do widget (as do `Selo` do sistema); `acento` é o verde da Malgo. */
/** Ficha 2026-11-04: canto inferior do botão e menu de assuntos do widget. */
export const POSICOES_WIDGET = ['direita', 'esquerda'] as const;
export const MAX_ASSUNTOS_WIDGET = 8;
export const TAMANHO_ASSUNTO_WIDGET = 60;
export const CORES_WIDGET = ['acento', 'azul', 'ciano', 'roxo', 'laranja', 'verde'] as const;
export type CorWidget = (typeof CORES_WIDGET)[number];

const ROTULO = /^(?!-)[a-z0-9-]{1,63}(?<!-)$/;
const IPV4 = /^\d{1,3}(\.\d{1,3}){3}$/;
/** Nomes da máquina local: aceitos sem ponto e com `http` (desenvolvimento). */
const LOCAIS = new Set(['localhost', '127.0.0.1']);

function hostValido(hostname: string): boolean {
  if (LOCAIS.has(hostname)) return true;
  if (hostname.length > 253 || hostname.startsWith('[') || IPV4.test(hostname)) return false;
  const rotulos = hostname.split('.');
  return rotulos.length >= 2 && rotulos.every((r) => ROTULO.test(r)) && !/^\d+$/.test(rotulos[rotulos.length - 1]!);
}

/** Domínio do cadastro normalizado (minúsculas, IDN em punycode, sem esquema nem caminho), ou o erro do campo. */
export function normalizarDominio(bruto: string): Result<string> {
  const invalido = () => fail('widget.dominio_invalido', `Domínio inválido: ${bruto.slice(0, 80)}`, { campo: 'dominios' });
  let t = bruto.normalize('NFC').trim().toLowerCase();
  if (!t || t.length > 300) return invalido();
  const curinga = t.startsWith('*.');
  if (curinga) t = t.slice(2);
  const comEsquema = /^[a-z][a-z0-9+.-]*:\/\//.test(t);
  if (comEsquema && !/^https?:\/\//.test(t)) return invalido();
  let url: URL;
  try {
    url = new URL(comEsquema ? t : `https://${t}`);
  } catch {
    return invalido();
  }
  if (url.username || url.password) return invalido();
  if (/[*\s]/.test(url.host) || !hostValido(url.hostname)) return invalido();
  if (curinga && (url.port || LOCAIS.has(url.hostname))) return invalido();
  return ok(curinga ? `*.${url.hostname}` : url.host);
}

/** Lista do cadastro: cada domínio normalizado, sem repetir, linhas vazias ignoradas, até `MAX_DOMINIOS_WIDGET`. */
export function normalizarDominios(lista: readonly string[]): Result<string[]> {
  const saida: string[] = [];
  for (const d of lista) {
    if (!d.trim()) continue;
    const n = normalizarDominio(d);
    if (!n.ok) return n;
    if (!saida.includes(n.valor)) saida.push(n.valor);
  }
  if (saida.length > MAX_DOMINIOS_WIDGET) return fail('widget.dominios_demais', `Até ${MAX_DOMINIOS_WIDGET} domínios por widget`, { campo: 'dominios', max: MAX_DOMINIOS_WIDGET });
  return ok(saida);
}

/**
 * A `Origin` da requisição é de um site do widget? Devolve a origem aceita, ou null. Só a forma exata de origem
 * (`https://host[:porta]`, sem caminho), em `https`; `http` só para a máquina local. Origem ausente, `null` (página de
 * arquivo, iframe isolado) ou de outro site: null.
 */
export function origemPermitida(origem: string | null | undefined, dominios: readonly string[]): string | null {
  if (!origem || origem === 'null' || origem.length > 300) return null;
  let url: URL;
  try {
    url = new URL(origem);
  } catch {
    return null;
  }
  if (url.origin !== origem) return null;
  if (url.protocol !== 'https:' && !(url.protocol === 'http:' && LOCAIS.has(url.hostname))) return null;
  const casa = dominios.some((d) => {
    if (!d.startsWith('*.')) return url.host === d;
    const sufixo = d.slice(1);
    return !url.port && url.hostname.endsWith(sufixo) && url.hostname.length > sufixo.length;
  });
  return casa ? url.origin : null;
}
